import { create } from "zustand";
import { axiosInstance } from "../lib/axios";

/**
 * Whether ERP can be trusted right now.
 *
 * Polling is reference-counted: components call `watch()` when they mount and
 * `unwatch()` when they leave, and the timer only runs while at least one is
 * on screen. A status indicator that keeps polling from a closed tab is load on
 * the system it is supposed to be protecting.
 *
 * The server answers from its own cached probe, so this poll is cheap — it does
 * not reach ERP. `check({ force: true })` is the exception, used immediately
 * before a submission, where a stale answer is not good enough.
 */

export type ErpConnectionState =
  | "Not Configured"
  | "Checking"
  | "Connected"
  | "Unstable"
  | "Connection Lost";

export interface ErpConnection {
  state: ErpConnectionState;
  /** False whenever a submission could not be guaranteed to land. */
  canSubmit: boolean;
  reason: string;
  lastCheckedAt: string | null;
  lastOkAt: string | null;
  samples: number;
  failureRate?: number;
  avgLatencyMs?: number | null;
  consecutiveFailures?: number;
  incident: {
    kind: "outage" | "unstable";
    since: string;
    lastError: string;
    blockedSubmissions: number;
  } | null;
}

const POLL_MS = 30000;

interface State {
  connection: ErpConnection | null;
  isChecking: boolean;
  watchers: number;
  timer: ReturnType<typeof setInterval> | null;

  check: (opts?: { force?: boolean }) => Promise<ErpConnection | null>;
  watch: () => void;
  unwatch: () => void;
}

export const useErpConnectionStore = create<State>((set, get) => ({
  connection: null,
  isChecking: false,
  watchers: 0,
  timer: null,

  check: async ({ force = false } = {}) => {
    set({ isChecking: true });
    try {
      const res = await axiosInstance.get("/erp/health", { params: force ? { force: "true" } : {} });
      set({ connection: res.data });
      return res.data as ErpConnection;
    } catch {
      // The status endpoint itself being unreachable is not the same as ERP
      // being down, and must not be reported as though it were. The caller is
      // told nothing is known, which blocks submission just as a failure would.
      const unknown: ErpConnection = {
        state: "Checking",
        canSubmit: false,
        reason: "Could not reach Omni Workspace to check the ERP connection.",
        lastCheckedAt: null,
        lastOkAt: null,
        samples: 0,
        incident: null,
      };
      set({ connection: unknown });
      return unknown;
    } finally {
      set({ isChecking: false });
    }
  },

  watch: () => {
    const next = get().watchers + 1;
    set({ watchers: next });
    if (next === 1) {
      get().check();
      set({ timer: setInterval(() => get().check(), POLL_MS) });
    }
  },

  unwatch: () => {
    const next = Math.max(0, get().watchers - 1);
    set({ watchers: next });
    if (next === 0) {
      const t = get().timer;
      if (t) clearInterval(t);
      set({ timer: null });
    }
  },
}));
